import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiPlus, FiEdit, FiTrash2, FiEye, FiFilter, FiCheckCircle } from 'react-icons/fi';
import { toast } from 'react-toastify';
import dependencyService from '../../services/dependencyService';
import CommonModal from '../../services/CommonModal';
import { FaFileExcel } from 'react-icons/fa';

const Dependencies = () => {
  const navigate = useNavigate();
  const [dependencies, setDependencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedDependency, setSelectedDependency] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    entity_type: '',
    priority: '',
    page: 1,
    limit: 20
  });
  const [pagination, setPagination] = useState({});
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchDependencies();
  }, [filters]);

  const fetchDependencies = async () => {
    try {
      setLoading(true);
      const response = await dependencyService.getAll(filters);
      setDependencies(response.data);
      setPagination(response.pagination);
    } catch (error) {
      toast.error('Failed to fetch dependencies');
      console.error('Error fetching dependencies:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await dependencyService.delete(id);
      toast.success('Dependency deleted successfully');
      fetchDependencies();
    } catch (error) {
      toast.error('Failed to delete dependency');
      console.error('Error deleting dependency:', error);
    }
  };

  const confirmDelete = () => {
    handleDelete(selectedDependency.id);
    setShowDeleteModal(false);
    setSelectedDependency(null);
  };

  const handleResolve = async (id) => {
    const notes = prompt('Enter resolution notes:');
    if (notes) {
      try {
        await dependencyService.resolve(id, notes);
        toast.success('Dependency resolved successfully');
        fetchDependencies();
      } catch (error) {
        toast.error('Failed to resolve dependency');
        console.error('Error resolving dependency:', error);
      }
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      Active: 'bg-blue-100 text-blue-800',
      Resolved: 'bg-green-100 text-green-800',
      Blocked: 'bg-red-100 text-red-800',
      Cancelled: 'bg-gray-100 text-gray-800'
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityBadge = (priority) => {
    const priorityColors = {
      Low: 'badge-info',
      Medium: 'badge-warning',
      High: 'badge-danger',
      Critical: 'badge-danger-dark'
    };
    return priorityColors[priority] || 'bg-gray-100 text-gray-800';
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      entity_type: '',
      priority: '',
      page: 1,
      limit: 20
    });
  };

  const handleDownloadDependencyList = () => {
    if (!dependencies || dependencies.length === 0) {
      toast.error('No dependencies to download');
      return;
    }

    const headers = [
      'Type', 'From', 'Depends On', 'Dependency Type', 'Status', 'Priority'
    ];

    const escapeCsv = (value) => {
      if (value === null || value === undefined) return '';
      const str = String(value);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const rows = dependencies.map((dependency) => [
      dependency.entity_type,
      dependency.project?.name || dependency.task?.title || 'N/A',
      dependency.dependent_on_project?.name ||
      dependency.dependent_on_task?.title ||
      dependency.external_reference || 'N/A',
      dependency.dependency_type,
      dependency.status,
      dependency.priority
    ]);

    const csvContent = [headers, ...rows]
      .map((row) => row.map(escapeCsv).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `dependencies-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success('Dependency list downloaded');
  };

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dependencies</h1>
          <p className="text-gray-600 mt-1">Manage project and task dependencies</p>
        </div>
        <div className="flex items-center space-x-3">
            <Link
            to="/app/dependencies/new"
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
          >
            <FiPlus className="w-5 h-5" />
            <span>Add Dependency</span>
          </Link>
          <button
            onClick={handleDownloadDependencyList}
             className="px-4 py-3 bg-white border border-green-600 text-green-600 rounded-lg flex items-center gap-2"
            title="Download dependency list"
          >
            <FaFileExcel />
          </button>
        </div>
      </div>

      <CommonModal
        isOpen={showDeleteModal}
        title="Delete Dependency"
        message="Are you sure you want to delete this dependency?"
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedDependency(null);
        }}
        onConfirm={confirmDelete}
        type="error"
        confirmText="Delete"
        cancelText="Cancel"
      />

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="flex justify-between items-center mb-4">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center space-x-2 text-gray-700 hover:text-gray-900"
          >
            <FiFilter className="w-5 h-5" />
            <span>Filters</span>
          </button>
          {(filters.status || filters.entity_type || filters.priority) && (
            <button
              onClick={clearFilters}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Clear Filters
            </button>
          )}
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Statuses</option>
                <option value="Active">Active</option>
                <option value="Resolved">Resolved</option>
                <option value="Blocked">Blocked</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Entity Type
              </label>
              <select
                value={filters.entity_type}
                onChange={(e) => handleFilterChange('entity_type', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Types</option>
                <option value="Project">Project</option>
                <option value="Task">Task</option>
                <option value="Milestone">Milestone</option>
                <option value="External">External</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Priority
              </label>
              <select
                value={filters.priority}
                onChange={(e) => handleFilterChange('priority', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Priorities</option>
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Critical">Critical</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Dependencies List */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : dependencies.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <p className="text-gray-500 text-lg">No dependencies found</p>
          <Link
            to="/app/dependencies/new"
            className="mt-4 inline-block text-blue-600 hover:text-blue-800"
          >
            Create your first dependency
          </Link>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    From
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Depends On
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Dependency Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Priority
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {dependencies.map((dependency) => (
                  <tr key={dependency.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-medium text-gray-900">
                        {dependency.entity_type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {dependency.project?.name || dependency.task?.title || 'N/A'}
                      </div>
                      <div className="text-xs text-gray-500">
                        {dependency.project?.code || ''}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {dependency.dependent_on_project?.name ||
                          dependency.dependent_on_task?.title ||
                          dependency.external_reference || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-600">
                        {dependency.dependency_type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadge(dependency.status)}`}>
                        {dependency.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getPriorityBadge(dependency.priority)}`}>
                        {dependency.priority}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => navigate(`/app/dependencies/${dependency.id}`)}
                          className="text-blue-600 hover:text-blue-900"
                          title="View"
                        >
                          <FiEye className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => navigate(`/app/dependencies/${dependency.id}/edit`)}
                          className="text-yellow-600 hover:text-yellow-900"
                          title="Edit"
                        >
                          <FiEdit className="w-5 h-5" />
                        </button>
                        {dependency.status === 'Active' && (
                          <button
                            onClick={() => handleResolve(dependency.id)}
                            className="text-green-600 hover:text-green-900"
                            title="Resolve"
                          >
                            <FiCheckCircle className="w-5 h-5" />
                          </button>
                        )}
                        <button
                          onClick={() => {
                            setSelectedDependency(dependency);
                            setShowDeleteModal(true);
                          }}
                          className="text-red-600 hover:text-red-900"
                          title="Delete"
                        >
                          <FiTrash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="mt-6 flex justify-between items-center">
              <div className="text-sm text-gray-700">
                Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                {pagination.total} results
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setFilters(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page === 1}
                  className="px-4 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setFilters(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page === pagination.pages}
                  className="px-4 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Dependencies;